import {createContext, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux';
import {HubConnectionBuilder, IHttpConnectionOptions, HubConnectionState} from '@microsoft/signalr';
import { remoteRoutes} from '../constants'
import {IState} from '../types'
import {getToken} from '../../utils/ajax';
import {IPersonProfile} from '../../app/modules/kyc/person/interfaces/IPersonProfile';
import {ICompanyProfile} from '../../app/modules/kyc/company/interfaces/ICompanyProfile';
import {KYC_VERIFICATION_CONSTANTS} from '../redux/verification/reducer';
import { STORE_CRM_PERSON_CONSTANTS } from '../redux/crmPersons/workflowsReducer';
import { STORE_CRM_COMPANY_CONSTANTS } from '../redux/crmCompanies/workflowsReducer';
import { REDUX_CRM_PERSONS_CONSTANTS } from '../redux/crmPersons/reducer';
import { REDUX_CRM_COMPANIES_CONSTANTS } from '../redux/crmCompanies/reducer';
import { DOWNLOADS_CONSTANTS } from '../redux/downloads/reducer';
import { REDUX_AUDIT_TRAIL } from '../redux/auditTrailReducer';


const WebSocketContext = createContext(null)

export {WebSocketContext}

interface IProps {
    children: any
}

export default ({children}: IProps) => {
    const userProfile = useSelector((state: IState) => state.core);
    const [connection, setConnection]: any = useState();
    const [kycConnection, setKycConnection]: any = useState();
    const [crmConnection, setCrmConnection]: any = useState();
    const [csvConnection, setCsvConnection]: any = useState();
    const [atConnection, setAtConnection]: any = useState();
    let webSocket: any;
    const dispatch = useDispatch();

    const joinKycGroup = async (groupName: any) => {
        try {
            await kycConnection.invoke('AddToGroup', groupName);
        } catch (error) {
            console.error(error);
        }
    };

    const joinCrmGroup = async (groupName: any) => {
        try {
            await crmConnection.invoke('AddToGroup', groupName);
        } catch (error) {
            console.error(error);
        }
    };

    const joinCsvGroup = async (groupName: any) => {
        try {
            await csvConnection.invoke('AddToGroup', groupName);
        } catch (error) {
            console.error(error);
        }
    };

    const joinAtGroup = async (groupName: any) => {
        try {
            await atConnection.invoke('AddToGroup', groupName);
        } catch (error) {
            console.error(error);
        }
    };

    const removeFromGroup = async (groupName: any) => {
        try {
            await connection.invoke('RemoveFromGroup', groupName);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const CRMConnection = new HubConnectionBuilder()        
            .withUrl(remoteRoutes.webSocketsCRM, {
                accessTokenFactory: () => (
                    getToken()
                )
            } as IHttpConnectionOptions)
            .withAutomaticReconnect()
            .build();
            setCrmConnection(CRMConnection);

        const KYCConnection = new HubConnectionBuilder()
            .withUrl(remoteRoutes.webSocketsKYC, {
                accessTokenFactory: () => (
                    getToken()
                )
            } as IHttpConnectionOptions)
            .withAutomaticReconnect()
            .build();
            setKycConnection(KYCConnection);

        const CSVConnection = new HubConnectionBuilder()        
            .withUrl(remoteRoutes.webSocketsCSV, {
                accessTokenFactory: () => (
                    getToken()
                )
            } as IHttpConnectionOptions)
            .withAutomaticReconnect()
            .build();
            setCsvConnection(CSVConnection);

        const ATConnection = new HubConnectionBuilder()        
            .withUrl(remoteRoutes.webSocketsAT, {
                accessTokenFactory: () => (
                    getToken()
                )
            } as IHttpConnectionOptions)
            .withAutomaticReconnect()
            .build();
            setAtConnection(ATConnection);
            
        // Cleanup function for unmounting
        return () => {
            CRMConnection.stop();
            KYCConnection.stop();
            CSVConnection.stop();
            ATConnection.stop();
        };
    }, []);

    const startKycConnection = async () => {
        if (!kycConnection) return;
        try {
            if (kycConnection.state === HubConnectionState.Disconnected) {
                await kycConnection.start();
            }

            if (kycConnection.state === HubConnectionState.Connected) {
                joinKycGroup(userProfile.user.tenantid)
                // Remove previous listeners to prevent duplicates
                kycConnection.off('ReceiveLastRunChecks');
                kycConnection.on('ReceiveLastRunChecks', (message: any) => {
                    dispatch({
                        type: KYC_VERIFICATION_CONSTANTS.REQUEST_DETAILS,
                        payload: message
                    });
                });
            }

        } catch (err) {
            console.log(err);
            setTimeout(() => startKycConnection(), 5000);
        }
    }

    const startCrmConnection = async () => {
        if (!crmConnection) return;
        try {
            if (crmConnection.state === HubConnectionState.Disconnected) {
                await crmConnection.start();
            }
            if (crmConnection.state === HubConnectionState.Connected) {
                joinCrmGroup(userProfile.user.tenantid)
                crmConnection.off('ReceiveCustomerProfile');
                crmConnection.on('ReceiveCustomerProfile', (message: any) => {
                    
                    if (message?.personProfile) {
                        const P: IPersonProfile  = message?.personProfile
                        dispatch({
                            type: STORE_CRM_PERSON_CONSTANTS.crmPersonReceiveProfile,
                            payload: P,
                        });
                
                        dispatch({
                            type: REDUX_CRM_PERSONS_CONSTANTS.CrmPersonRequestsReceiveProfile,
                            payload: P,
                        });
                    }

                    if (message?.companyProfile) {
                        const C: ICompanyProfile = message?.companyProfile
                        dispatch({
                            type: STORE_CRM_COMPANY_CONSTANTS.workflowsReceiveProfile,
                            payload: C,
                        });

                        dispatch({
                            type: REDUX_CRM_COMPANIES_CONSTANTS.RequestsReceiveProfile,
                            payload: C,
                        });
                    }
                });
            }

        } catch (err) {
            console.log(err);
            setTimeout(() => startCrmConnection(), 5000);
        }
    }

    const startCsvConnection = async () => {
        if (!csvConnection) return;
        try {
            if (csvConnection.state === HubConnectionState.Disconnected) {
                await csvConnection.start();
            }
            if (csvConnection.state === HubConnectionState.Connected) {
                joinCsvGroup(userProfile.user.tenantid)
                csvConnection.off('ReceiveExport');
                csvConnection.on('ReceiveExport', (message: any) => {

                    dispatch({
                        type: DOWNLOADS_CONSTANTS.READY,
                        payload: message.response
                    });
                });
            }

        } catch (err) {
            console.log(err);
            setTimeout(() => startCsvConnection(), 5000);
        }
    }

    const startATConnection = async () => {
        if (!atConnection) return;
        try {
            if (atConnection.state === HubConnectionState.Disconnected) {
                await atConnection.start();
            }
            if (atConnection.state === HubConnectionState.Connected) {
                joinAtGroup(userProfile.user.tenantid)
                atConnection.off('ReceiveAuditTrails');
                atConnection.on('ReceiveAuditTrails', (message: any) => {
                    dispatch({
                        type: REDUX_AUDIT_TRAIL.FetchAll,
                        payload: message?.audits
                    });
                });
            }

        } catch (err) {
            console.log(err);
            setTimeout(() => startATConnection(), 5000);
        }
    }

    // Trigger connections when the user or connections become available
    useEffect(() => {
        if (userProfile.user) {
            startKycConnection();
            startCrmConnection();
            startCsvConnection();
            startATConnection();
        } else {
            // Stop connections if user logs out
            kycConnection?.stop();
            crmConnection?.stop();
            csvConnection?.stop();
            atConnection?.stop();
        }
    }, [userProfile.user, kycConnection, crmConnection, csvConnection, atConnection]);

    // Attach onclose reconnect handlers inside a useEffect
    useEffect(() => {
        if (kycConnection) kycConnection.onclose(() => userProfile.user && startKycConnection());
        if (crmConnection) crmConnection.onclose(() => userProfile.user && startCrmConnection());
        if (csvConnection) csvConnection.onclose(() => userProfile.user && startCsvConnection());
        if (atConnection) atConnection.onclose(() => userProfile.user && startATConnection());
    }, [kycConnection, crmConnection, csvConnection, atConnection, userProfile.user]);

    webSocket = {
        connection,
        kycConnection,
        crmConnection,
        csvConnection,
        atConnection
    }

    return (
        <WebSocketContext.Provider value={webSocket}>
            {children}
        </WebSocketContext.Provider>
    )
}